/**
 * Learner-facing adaptive question state badge.
 *
 * Renders one of three adaptive states and exposes a short explanation on
 * hover (title) and tap/click (inline popover).
 *
 * @module AdaptiveQuestionStateBadge
 */

import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { AdaptiveQuestionState } from '@/types/exam-simulation';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface AdaptiveQuestionStateBadgeProps {
  readonly state: AdaptiveQuestionState;
  readonly className?: string;
}

export function AdaptiveQuestionStateBadge({ state, className }: AdaptiveQuestionStateBadgeProps) {
  const { t } = useTranslation();

  const label = t(`simulation.adaptiveState.${state}.label`);
  const description = t(`simulation.adaptiveState.${state}.description`);

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn('inline-flex items-center', className)}
            title={description}
            aria-label={`${label}. ${description}`}
          >
            <Badge variant="outline" className="font-ui gap-1.5 bg-background/90 text-xs">
              {label}
              <Info className="h-3 w-3 text-muted-foreground" />
            </Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-56 text-xs font-ui leading-relaxed">
          {description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
